import { request } from "umi";
import { paramsSerializer } from "../../api";

export type LoginAsResult = {
  token: string;
  expires_at: string;
  expires_in_seconds: number;
  user: { id: number; name: string };
};

export type LoginAsLogRow = {
  id: number;
  admin_user_id: number;
  admin_name: string;
  target_user_id: number;
  target_name: string;
  reason: string | null;
  ip_address: string | null;
  started_at: string;
  expires_at: string;
  ended_at: string | null;
  ended_reason: string | null;
  actions_count: number;
  status: "active" | "ended";
};

export type LoginAsLogAction = {
  id: number;
  impersonation_log_id: number;
  method: string;
  path: string;
  created_at: string;
};

export type LoginAsLogFilterOption = { id: number; name: string };

export type LoginAsCandidate = {
  id: number;
  name: string;
  email: string;
  roles: string[];
  /** Set when a per-user override still blocks it; the role hierarchy already allows it */
  blocked_reason: string | null;
};

/**
 * Start a "Log In As User" session — returns a 60-minute token for the target.
 *
 * POST /api/tenant/users/{id}/login-as
 */
export async function loginAsUser(id: number, data: { reason?: string }) {
  return request<API.ResultObject<LoginAsResult>>(`/api/tenant/users/${id}/login-as`, {
    method: "POST",
    data,
    withToken: true,
  });
}

/**
 * People the current user may log in as (role hierarchy applied server-side).
 *
 * GET /api/tenant/users/login-as/candidates
 */
export async function getLoginAsCandidates(params: API.PageParams) {
  return request<API.ResultObject<API.PaginatedResult<LoginAsCandidate>>>(`/api/tenant/users/login-as/candidates`, {
    method: "GET",
    params: {
      keyWords: params.keyWords,
      perPage: params.pageSize,
      page: params.current,
    },
    withToken: true,
  }).then((res) => ({
    data: res.data?.rows ?? [],
    success: res.success ?? false,
    total: res.data?.pagination?.total_records ?? 0,
  }));
}

/**
 * End the current Log In As session: a normal logout of the user, sent with
 * the impersonation token itself (the backend also closes the log row).
 *
 * POST /api/auth/logout
 */
export async function endLoginAs() {
  return request<API.ResultObject<any>>(`/api/auth/logout`, {
    method: "POST",
    withToken: true,
    skipErrorHandler: true,
  });
}

/**
 * GET /api/tenant/users/login-as/log
 */
export async function getLoginAsLog(params: API.PageParams & Record<string, any>, sort?: any) {
  return request<API.ResultObject<API.PaginatedResult<LoginAsLogRow>>>(`/api/tenant/users/login-as/log`, {
    method: "GET",
    params: {
      ...params,
      perPage: params.pageSize,
      page: params.current,
      sort,
    },
    withToken: true,
    paramsSerializer,
  }).then((res) => ({
    data: res.data?.rows ?? [],
    success: res.success ?? false,
    total: res.data?.pagination?.total_records ?? 0,
  }));
}

/**
 * GET /api/tenant/users/login-as/log/filters
 */
export async function getLoginAsLogFilters() {
  return request<API.ResultObject<{ admins: LoginAsLogFilterOption[]; targets: LoginAsLogFilterOption[] }>>(
    `/api/tenant/users/login-as/log/filters`,
    {
      method: "GET",
      withToken: true,
    },
  );
}

/**
 * GET /api/tenant/users/login-as/log/{id}/actions
 */
export async function getLoginAsLogActions(id: number, params: API.PageParams) {
  return request<API.ResultObject<API.PaginatedResult<LoginAsLogAction>>>(`/api/tenant/users/login-as/log/${id}/actions`, {
    method: "GET",
    params: {
      perPage: params.pageSize,
      page: params.current,
    },
    withToken: true,
  }).then((res) => ({
    data: res.data?.rows ?? [],
    success: res.success ?? false,
    total: res.data?.pagination?.total_records ?? 0,
  }));
}
